/**
 * PatternDetector
 * Detects completion patterns for AI encouragement triggers
 */

import { CompletionEntry } from '../../models/CompletionEntry';
import { EncouragementPattern } from '../../models/EncouragementMessage';

/**
 * Calculate current streak from completion history
 */
export const calculateStreak = (completions: CompletionEntry[]): number => {
  if (completions.length === 0) return 0;

  // Sort by date descending (most recent first)
  const sorted = [...completions]
    .filter(c => c.completed)
    .sort((a, b) => b.date.localeCompare(a.date));

  if (sorted.length === 0) return 0;

  let streak = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 0; i < sorted.length; i++) {
    const entryDate = new Date(sorted[i].date);
    entryDate.setHours(0, 0, 0, 0);

    const expectedDate = new Date(today);
    expectedDate.setDate(today.getDate() - i);
    expectedDate.setHours(0, 0, 0, 0);

    if (entryDate.getTime() === expectedDate.getTime()) {
      streak++;
    } else {
      break;
    }
  }

  return streak;
};

/**
 * Calculate total completed days
 */
export const calculateTotalDays = (completions: CompletionEntry[]): number => {
  return completions.filter(c => c.completed).length;
};

/**
 * Calculate gap since last completion (in days)
 */
export const calculateGapDays = (completions: CompletionEntry[]): number => {
  const completed = completions
    .filter(c => c.completed)
    .sort((a, b) => b.date.localeCompare(a.date));

  if (completed.length === 0) return 0;

  const lastCompletionDate = new Date(completed[0].date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  lastCompletionDate.setHours(0, 0, 0, 0);

  const diffTime = Math.abs(today.getTime() - lastCompletionDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays - 1; // Subtract 1 because same day = 0 gap
};

/**
 * Check if user completed 7 consecutive days
 */
export const checkPerfectWeek = (completions: CompletionEntry[]): boolean => {
  const last7Days = completions.slice(0, 7);
  return last7Days.length === 7 && last7Days.every(c => c.completed);
};

/**
 * Detect which pattern should trigger encouragement
 */
export const detectPattern = (
  completions: CompletionEntry[],
  previousStreak: number
): EncouragementPattern | null => {
  const currentStreak = calculateStreak(completions);
  const totalDays = calculateTotalDays(completions);
  const gapDays = calculateGapDays(completions);

  console.log('Pattern detection:', {
    currentStreak,
    previousStreak,
    totalDays,
    gapDays,
    completionsCount: completions.length,
  });

  // First completion ever - disabled
  // if (totalDays === 1) {
  //   console.log('Pattern detected: first_completion');
  //   return 'first_completion';
  // }

  // Return after gap (14+ days since last completion)
  if (gapDays >= 14 && previousStreak === 0 && currentStreak === 1) {
    return 'return_after_gap';
  }

  // Perfect week (7 consecutive days)
  if (checkPerfectWeek(completions) && previousStreak === 6 && currentStreak === 7) {
    return 'perfect_week';
  }

  // 7-day streak
  if (currentStreak === 7 && previousStreak === 6) {
    return 'streak_7';
  }

  // 30-day streak
  if (currentStreak === 30 && previousStreak === 29) {
    return 'streak_30';
  }

  // Milestones: 10, 25, 50, 100 total completions
  if (
    (totalDays === 10 || totalDays === 25 || totalDays === 50 || totalDays === 100) &&
    totalDays > previousStreak
  ) {
    return 'milestone';
  }

  return null;
};

/**
 * Get pattern description for display
 */
export const getPatternDescription = (pattern: EncouragementPattern): string => {
  switch (pattern) {
    case 'first_completion':
      return 'First goal completed';
    case 'return_after_gap':
      return 'Returned after a break';
    case 'perfect_week':
      return '7 days in a row';
    case 'streak_7':
      return '7-day streak';
    case 'streak_30':
      return '30-day streak';
    case 'milestone':
      return 'Milestone reached';
    default:
      return 'Pattern detected';
  }
};
